import _, { isEmpty, omit } from 'lodash';
import i18n from 'i18next';
import { Limitation, LimitationData, ServerLimitation, ServerLimitationItem } from 'core/limitation/Limitation';
import { rtbInventories } from 'containers/Limitations/LimitationSetting/rtbLimitationInventory';
import { OPERATES } from 'enum/Operate';
import { SelectOptions } from 'components/commonType';
import { KeywordSource } from 'core/product/Product';

const KEYWORD_LIMITATION_TYPES = new Set(['searchKeywords', 'recommendedKeywords']);

const fallbackSourcesFor = (type: string): KeywordSource[] =>
  type === 'searchKeywords' ? [KeywordSource.MANUAL] : [KeywordSource.SYSTEM];

const toServerLimit = (limit: any, type: string): ServerLimitationItem => {
  const stripped = omit(limit, ['isGroup', 'extra']) as SelectOptions;
  if (KEYWORD_LIMITATION_TYPES.has(type)) {
    const sources = limit.extra?.sources;
    const sourcesArray = Array.isArray(sources) && sources.length > 0
      ? sources
      : fallbackSourcesFor(type);
    return { ...stripped, sources: sourcesArray };
  }
  return stripped;
};

const transformLimitationData = (op: string, data: Limitation): ServerLimitation[] => {
  if (!Array.isArray(data.value)) {
    return [
      {
        op: op,
        limits: data.value,
        isGroup: false,
        type: data.type
      }
    ];
  }
  const limits = [...data.value];
  const groupData = _.remove(limits, (value: any) => value.isGroup === true);
  let result = limits.length > 0 ? [{
    op: op,
    limits: limits.map(limit => toServerLimit(limit, data.type)),
    isGroup: false,
    type: data.type
  }] : [];
  if (groupData.length > 0) {
    result.push({
      op: op,
      limits: groupData.map(limit => toServerLimit(limit, data.type)),
      isGroup: true,
      type: data.type
    });
  }
  return result;
};

export function toServerStructure (limitations: LimitationData, filterTA: {[key: string]: string[]} = {
  other: ['dealId'],
  include: ['age_min', 'age_max']
}) {

  const otherFilter = _.defaultTo(filterTA.other, []);
  const otherLimitations = _.filter(
    _.defaultTo(limitations.other, []),
    (limitation: Limitation) => !otherFilter.includes(limitation.type)
  );

  const includeFilter = _.defaultTo(filterTA.include, []);
  const includeLimitations = _.filter(
    _.defaultTo(limitations.include, []),
    (limitation: Limitation) => !includeFilter.includes(limitation.type)
  );

  let result = _.compact(_.flatten(_.concat(
    includeLimitations.map(value => {
      return transformLimitationData('inc', value);
    }),
    _.defaultTo(limitations.exclude, []).map(value => {
      return transformLimitationData('exc', value);
    }),
    _.defaultTo(limitations.preferred, []).map(value => {
      return transformLimitationData('Preferred', value);
    }),
    _.defaultTo(limitations.nonPreferred, []).map(value => {
      return transformLimitationData('NonPreferred', value);
    }),
    // dealId
    otherLimitations.map(value => {
      if (value.type === 'dealId') {
        return transformLimitationData('inc', value);
      } else {
        return transformLimitationData(value.op ? value.op : 'other', value);
      }
    }))
  ));
  return result;
}

export function getLimitationContentLabel (type: string, limitation: { value: string | number, label: string }) {
  const i18nValue = limitation.value.toString().toLowerCase().replace(/-|\s/g, '_');
  const inventoryData: any = rtbInventories[type] ? rtbInventories[type] : {};
  const i18nPrefix = inventoryData.i18nPrefix;
  return i18nPrefix ? i18n.t<string>(`${i18nPrefix}.${i18nValue}`) : limitation.label;
}

export function wrapLimitationsFromServer (limitations: ServerLimitation[], dealIds?: string[], ageMin?: number, ageMax?: number): LimitationData {
  const limitationMap = {
    inc: {},
    exc: {},
    Preferred: {},
    NonPreferred: {},
    other: {}
  };
  limitations.forEach(limitation => {
    if (limitation.type === 'age') {
      return;
    }
    const limits = _.get(limitation, 'limits', []);
    const isGroup = _.get(limitation, 'isGroup');
    const type = _.get(limitation, 'type');
    const isKeywordType = KEYWORD_LIMITATION_TYPES.has(type);
    const wrappedLimitation = {
      op: _.get(limitation, 'op'),
      value: Array.isArray(limits) ? limits.map(item => {
        const wrapped: any = {
          label: _.get(item, 'label'),
          value: _.get(item, 'value'),
          isGroup
        };
        if (isKeywordType) {
          const sources = _.get(item, 'sources');
          if (Array.isArray(sources) && sources.length > 0) {
            wrapped.extra = { sources };
          }
        }
        return wrapped;
      }) : limits,
      type
    };
    if (limitation.type === 'dealId') {
      limitationMap['other'][limitation.type] = wrappedLimitation;
    } else if (limitation.type in limitationMap[limitation.op]) {
      limitationMap[limitation.op][limitation.type].value = _.concat(limitationMap[limitation.op][limitation.type].value, wrappedLimitation.value);
    } else {
      limitationMap[limitation.op][limitation.type] = wrappedLimitation;
    }
  });

  const wrappedLimitations: LimitationData = {
    include: Object.values(limitationMap['inc']),
    exclude: Object.values(limitationMap['exc']),
    preferred: Object.values(limitationMap['Preferred']),
    nonPreferred: Object.values(limitationMap['NonPreferred']),
    other: Object.values(limitationMap['other'])
  };

  const ageOptValue = _.find(wrappedLimitations.include, (limitation: Limitation) => limitation.type === 'age') ? 'include' : 'preferred';

  ageMin && wrappedLimitations[ageOptValue].push({
    op: ageOptValue === 'include' ? 'inc' : 'Preferred',
    value: ageMin,
    type: 'age_min'
  });

  ageMax && wrappedLimitations[ageOptValue].push({
    op: ageOptValue === 'include' ? 'inc' : 'Preferred',
    value: ageMax,
    type: 'age_max'
  });

  if (dealIds && dealIds.length > 0) {
    const dealIdTA = wrappedLimitations.other.find(ta => ta.type === 'dealId');
    !dealIdTA && wrappedLimitations.other.push({
      op: 'inc',
      value: dealIds ? dealIds.map(dealId => {
        return {
          label: dealId,
          value: dealId
        };
      }) : [],
      type: 'dealId'
    });
  }

  return wrappedLimitations;
}

export function getLimitationValueContent (limitationValue: LimitationData, op: OPERATES | 'other', type: string): string[] {
  const operateTA: Limitation[] = _.get(limitationValue, op, []);
  const typeTA = _.find(operateTA, (ta: Limitation) => ta.type === type);
  const value = typeTA ? typeTA.value : [];
  if (value instanceof String || value instanceof Number) {
    return [value.toString()];
  }
  if (value instanceof Array) {
    return value.map((value: SelectOptions) => value.value.toString());
  }
  if (isEmpty(value)) {
    return [];
  }
  return [value.toString()];
}
